import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  FolkloreType,
  FoodType,
  User,
} from '../../../../../apollo/__generated__/graphql';
import { useToast } from '../../../../../toast/Toast.context';
import { useUpdateUserMutation } from '../../../../hooks/useUpdateUserMutation';

interface UpdateUserFormInputs {
  firstname: string;
  lastname: string;
  nickname?: string;
  folklore: string;
  studyTown: string;
  studies: string;
  phone: string;
  externalReferentFirstname: string;
  externalReferentLastname: string;
  externalReferentPhone: string;
  birthdate: Date;
  food: string;
  allergies?: string;
  medication?: string;
}

const schema = yup
  .object({
    firstname: yup
      .string()
      .matches(
        /^[A-Za-zéèêëàâäùûüç\- ]+$/,
        'Seul des lettres sont autorisées pour Hello Asso',
      )
      .min(3, 'Le prénom doit faire au moins 3 caractères pour Hello Asso')
      .required('Le prénom est requis'),
    lastname: yup
      .string()
      .matches(
        /^[A-Za-zéèêëàâäùûüç\- ]+$/,
        'Seul des lettres sont autorisées pour Hello Asso',
      )
      .min(3, 'Le nom doit faire au moins 3 caractères pour Hello Asso')
      .required('Le nom est requis'),
    nickname: yup.string().optional(),
    folklore: yup.string().required('Le folklore est requis'),
    studyTown: yup.string().required("La ville d'étude est requise"),
    studies: yup.string().required('Les études sont requisent'),
    phone: yup.string().required('Le numéro de téléphone est requis'),
    externalReferentFirstname: yup
      .string()
      .required('Le prénom du référent est requis'),
    externalReferentLastname: yup
      .string()
      .required('Le nom du référent est requis'),
    externalReferentPhone: yup
      .string()
      .required('Le numéro de téléphone du référent est requis'),
    birthdate: yup.date().required('La date de naissance est requise'),
    food: yup.string().required('Le type de repas est requis'),
    allergies: yup.string().optional(),
    medication: yup.string().optional(),
  })
  .required();

export const useUserInfosForm = (currentUser: User) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { updateUser } = useUpdateUserMutation(() => {
    showToast({
      type: 'success',
      message: 'Vos informations ont bien été mises à jour',
    });
    navigate('/dashboard');
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<UpdateUserFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: currentUser?.firstname ?? '',
      lastname: currentUser?.lastname ?? '',
      nickname: currentUser?.nickname ?? '',
      folklore: currentUser?.folklore ?? FolkloreType.Faluchard,
      studyTown: currentUser?.studyTown ?? '',
      studies: currentUser?.studies ?? '',
      phone: currentUser?.phone ?? '',
      externalReferentFirstname: currentUser?.externalReferentFirstname ?? '',
      externalReferentLastname: currentUser?.externalReferentLastname ?? '',
      externalReferentPhone: currentUser?.externalReferentPhone ?? '',
      birthdate: new Date(currentUser?.birthdate),
      food: currentUser?.food ?? FoodType.Omnivore,
      allergies: currentUser?.allergies ?? '',
      medication: currentUser?.medication ?? '',
    },
  });

  const onValid: SubmitHandler<UpdateUserFormInputs> = async (data) => {
    const nickname =
      data.folklore === FolkloreType.Impetrant ||
      data.folklore === FolkloreType.Sympathisant ||
      data.nickname === ''
        ? null
        : data.nickname;
    updateUser({
      ...data,
      folklore: data.folklore as FolkloreType,
      food: data.food as FoodType,
      nickname,
      allergies: data.allergies !== '' ? data.allergies : null,
      medication: data.medication !== '' ? data.medication : null,
    });
  };
  const onSubmit = handleSubmit(onValid);

  const phoneValue = getValues('phone');
  const externalReferentPhoneValue = getValues('externalReferentPhone');
  const birthdateValue = getValues('birthdate');
  const folkloreValue = getValues('folklore');

  const onBirthdateChange = (date: Date | null) => {
    setValue('birthdate', date ?? new Date());
  };

  const onPhoneChange = (phone: string) => {
    setValue('phone', phone, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const onExternalReferentPhoneChange = (phone: string) => {
    setValue('externalReferentPhone', phone, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return {
    register,
    errors,
    onSubmit,
    onBirthdateChange,
    birthdateValue,
    isDirty,
    onPhoneChange,
    phoneValue,
    externalReferentPhoneValue,
    onExternalReferentPhoneChange,
    folkloreValue,
    control,
  };
};
